import { zodResolver } from "@hookform/resolvers/zod";
import { DialogClose, DialogTrigger } from "@radix-ui/react-dialog";
import { Pencil } from "lucide-react";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  type AssignedRole,
  type AssignmentsUserRole,
  useUpdateAssignmentRoleMutation,
} from "@/api/admin/users";
import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import {
  CustomButton,
  CustomCardBody,
  DialogBody,
  type ItemsListType,
} from "@/components/atoms";
import {
  SelectControl,
  toastError,
  toastSuccess,
} from "@/components/molecules";
import { Dialog } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { commonIconProps12 } from "@/utils/constants";
import { formatEnumValue } from "@/utils/formatEnumValue";
import { usePartnerOptions } from "@/utils/hooks/usePartnerOptions";

import { editRoleSchema, type EditRoleType } from "./types";

interface EditRoleProps {
  data: AssignedRole;
  rolesList: ItemsListType;
}

const EditRole: FC<EditRoleProps> = ({ data, rolesList }) => {
  const { id = "" } = useParams();
  const { levelsList, isOptionPending } = usePartnerOptions();

  const { mutate, isPending } = useUpdateAssignmentRoleMutation();

  const form = useForm<EditRoleType>({
    resolver: zodResolver(editRoleSchema),
    defaultValues: {
      ...data,
    },
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  const onSubmit = (payload: EditRoleType) => {
    const selectedRole = rolesList.find(r => r.id === payload.roleId);

    const payloadFinal = {
      level: payload.level,
      // TODO: delete after backend is ready
      role: "ADMIN",
      // role: selectedRole?.name,
      roleId: selectedRole?.id,
      entityIds: [payload.entityId],
    };

    mutate(
      {
        id: data.id,
        data: payloadFinal as AssignmentsUserRole,
      },
      {
        onSuccess: () => {
          toastSuccess("Role was updated successfully!");
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.adminUserRole, id],
          });
        },
        onError: err => {
          toastError(err.message, () => onSubmit(payload), "Error");
        },
      }
    );
  };

  const {
    handleSubmit,
    formState: { isValid },
  } = form;

  if (isOptionPending) {
    return;
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="edit-role-assign-button"
          className="flex items-center gap-2 cursor-pointer px-2 py-1.5 w-full rounded-[6px] focus:bg-accent hover:bg-accent focus:text-accent-foreground text-xs"
        >
          <Pencil {...commonIconProps12} />
          <span>Edit role</span>
        </button>
      </DialogTrigger>
      <DialogBody title={`Edit “${data.name}” role`}>
        <div className="flex flex-col gap-5 pt-2">
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              data-testid="edit-role-form"
            >
              <CustomCardBody classes="pb-5 px-0">
                <div className="flex flex-col align-center gap-4.5">
                  <SelectControl<EditRoleType>
                    form={form}
                    nameSelect="level"
                    label="Level"
                    placeholder="Select"
                    itemsList={levelsList}
                    triggerProps={{ "data-testid": "edit-role-select-level" }}
                    disabled
                  />
                  <SelectControl<EditRoleType>
                    form={form}
                    nameSelect="entityId"
                    label={`${formatEnumValue(data.level)} name`}
                    itemsList={[{ name: data.name, id: data.entityId }]}
                    triggerProps={{ "data-testid": "edit-role-select-name" }}
                    disabled
                  />
                  <SelectControl<EditRoleType>
                    form={form}
                    nameSelect="roleId"
                    label="Role"
                    placeholder="Select"
                    itemsList={rolesList}
                    triggerProps={{ "data-testid": "edit-role-select-role" }}
                  />
                </div>
              </CustomCardBody>
              <div className="flex justify-between gap-3">
                <DialogClose asChild>
                  <CustomButton
                    variant="light"
                    type="button"
                    className="flex-1"
                    data-testid="edit-role-cancel-button"
                  >
                    Cancel
                  </CustomButton>
                </DialogClose>

                <DialogClose asChild>
                  <CustomButton
                    type="submit"
                    className="flex-1"
                    data-testid="edit-role-submit-button"
                    disabled={!isValid}
                    isPending={isPending}
                  >
                    Update Role
                  </CustomButton>
                </DialogClose>
              </div>
            </form>
          </Form>
        </div>
      </DialogBody>
    </Dialog>
  );
};

export default EditRole;
